import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import {
  TRANSITION_KINDS,
  setBannedPhrases,
  setBgIntro,
  setSessionIntroEnabled,
  setSessionIntroText,
  setTransitionClause,
  setTransitionShape,
  setTransitionSingle,
  type ToneDocument,
  type TransitionKind,
} from "@/lib/backgroundProfile";
import type { DraftPart } from "@/lib/assetContent";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Field, Input, Select, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { UnavailableState } from "@/ui/states";

/**
 * The named tone controls of a shared BG interview profile (#914).
 *
 * ## WHY THIS IS A FORM AND NOT A TEXT BOX OVER YAML
 *
 * `tone` is a STRUCTURED part, and `NamedDocumentEditor` deliberately refuses to
 * author one: `StructuredPart.data` is an unconstrained `JsonValue`, so a
 * writable textarea over its bytes would be "a clinician retyping the inside of
 * a document whose shape this repository does not own". #858 changed that
 * premise for this one role — `background_profile.ToneDocument` publishes the
 * exact five keys, their types and the two shapes the transition template may
 * take, pinned to the pinned engine's own `TONE_REQUIRED_KEYS` by
 * `test_background_profile_against_real_brain_checkout.py`. So this form renders
 * the service's published contract rather than inventing one, which is the same
 * warrant `BackgroundDomainPage` holds for the `domains` document.
 *
 * ## WHAT EACH CONTROL ACTUALLY DOES AT RUNTIME
 *
 * Named for the behaviour a clinician is choosing, with the engine's own
 * mechanism stated beside it rather than the pack key:
 *
 *  * **Session introduction** — `tracker._build_self_intro_directive` composes a
 *    standalone SELF_INTRO turn from the greeting and the ask. MOD disables it
 *    and opens straight into the background conversation instead, which is the
 *    posture S7 contract §4 names explicitly.
 *  * **Background opening** — `tracker._maybe_prepend_bg_intro` prepends this to
 *    the very first directive of the session, and only that one.
 *  * **Moving between areas** — `tracker._render_domain_change` picks one of
 *    three clauses by the shape of the transition.
 *  * **Phrases to avoid** — carried into the emitted document. This one gets a
 *    stated limit rather than an implied promise; see below.
 *
 * ## THE BANNED PHRASES SAY WHAT THEY ARE
 *
 * `banned_phrases` is INERT at the pinned engine: nothing in the pinned Brain
 * reads the key, and the ban is actually carried by the transition prose that
 * spells the same phrases out. It is part of the family because both source
 * packs carry it and equivalence compares the emitted file — not because this
 * service can promise the runtime enforces it. A control that let a clinician
 * believe they had suppressed a phrase would be worse than no control, so the
 * surface says which mechanism does the work.
 *
 * ## LANGUAGE
 *
 * One block per stored `tone` document, `NamedDocumentEditor`'s rule: a
 * monolingual profile renders exactly one form and a bilingual one renders two,
 * each labelled, rather than picking one and hiding the other. The language
 * label is a read-only fact about which document is which — never a control
 * that authors a key prefix.
 */

/** What each transition clause is FOR, in the terms a clinician chooses by. */
const TRANSITION_LABELS: Record<TransitionKind, string> = {
  opening: "When the next area opens with its own invitation",
  bare: "When the next area opens with a direct question",
  next_topic: "When the conversation is already inside the new area",
};

const TRANSITION_DESCRIPTIONS: Record<TransitionKind, string> = {
  opening:
    "The area's own opening already invites the patient to share, so this clause only says how to reach it.",
  bare: "The area opens on a gate question that names no topic.",
  next_topic: "No pivot is being made; the area is already the current one.",
};

/** How many of the three named clauses this document actually carries text in. */
function writtenClauses(view: { clauses: Record<TransitionKind, string> }): number {
  return TRANSITION_KINDS.filter((kind) => view.clauses[kind] !== "").length;
}

const SHAPE_DESCRIPTION =
  "Both source profiles name the three transitions separately. A profile may instead carry one instruction used for all three, which is the shape inherited profiles use.";

export function BackgroundToneForm({
  documents,
  parts,
  readOnly,
  onChange,
}: {
  /** Every stored `tone` document, in stored order. */
  documents: readonly ToneDocument[];
  parts: readonly DraftPart[];
  readOnly: boolean;
  onChange: (parts: DraftPart[]) => void;
}) {
  if (documents.length === 0) {
    return (
      <UnavailableState
        title="This profile carries no tone document"
        description="A shared interview profile stores its openings and transitions in a tone document, and this version has none at any language. Its two prompts are still readable on their own rows."
      />
    );
  }

  return (
    <div className="space-y-8">
      {documents.map((document) => (
        <ToneDocumentFields
          key={document.documentId}
          document={document}
          parts={parts}
          showLanguage={documents.length > 1}
          readOnly={readOnly}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

function ToneDocumentFields({
  document,
  parts,
  showLanguage,
  readOnly,
  onChange,
}: {
  document: ToneDocument;
  parts: readonly DraftPart[];
  showLanguage: boolean;
  readOnly: boolean;
  onChange: (parts: DraftPart[]) => void;
}) {
  const view = document.view;
  // HEADING LEVELS FOLLOW THE PAGE, not the visual size. The page's own `h1` is
  // the document name, so this form's groups are `h2` — unless a language
  // heading stands above them, which is itself the `h2` and makes the groups
  // `h3`. A fixed level was `h4` throughout and skipped two levels on a
  // monolingual profile, which is `heading-order` and the reason this is
  // computed rather than typed.
  const Group = showLanguage ? "h3" : "h2";

  if (view === null) {
    // THE BUFFER IS NOT PRESENTED AS EMPTY. `readToneDocument` answers `null`
    // for a document this form has no controls for, and rendering blank fields
    // over it would invite a clinician to save that blankness across a stored
    // version's only readable copy. The bytes ride through every other save
    // untouched, which is what `editDocument` guarantees.
    return (
      <UnavailableState
        title={
          showLanguage
            ? `The ${document.language ?? "language-neutral"} tone document is not in a shape this form can present`
            : "This tone document is not in a shape this form can present"
        }
        description="Its stored content does not match the published tone contract, so the named controls below it would be editing values they had not read. The document itself is unchanged and rides through any save made on this asset."
      />
    );
  }

  return (
    <section className="space-y-6">
      {showLanguage ? (
        <h2 className="text-sm font-medium text-foreground">
          Language: {document.language ?? "every language"}
        </h2>
      ) : null}

      <div className="space-y-4">
        <Group className="text-sm font-medium text-foreground">
          Session introduction
        </Group>
        <Field
          label="Opening turn"
          description="A separate first turn that greets the patient and asks one opening question, before any background area begins."
        >
          {({ inputId, describedBy }) => (
            <Select
              id={inputId}
              aria-describedby={describedBy}
              value={view.sessionIntro.enabled ? "enabled" : "disabled"}
              disabled={readOnly}
              onChange={(event) =>
                onChange(
                  setSessionIntroEnabled(
                    parts,
                    document.documentId,
                    event.currentTarget.value === "enabled",
                  ),
                )
              }
            >
              <option value="disabled">
                No separate opening turn — the interview starts with the
                background opening below
              </option>
              <option value="enabled">
                Ask a separate opening turn before the background areas
              </option>
            </Select>
          )}
        </Field>

        {/* THE TWO TEXTS STAY VISIBLE WHILE THE TURN IS OFF, deliberately.
            They are stored either way — MOD ships two empty strings beside
            `enabled: false` — and hiding them would make a clinician unable to
            see or clear what a re-enabled turn would say. */}
        <Field
          label="Greeting"
          description="Prepended to the opening turn. {firstname} is replaced with the patient's first name; every other brace is literal text."
        >
          {({ inputId, describedBy }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              rows={3}
              dir="auto"
              value={view.sessionIntro.greetingTemplate}
              readOnly={readOnly}
              onChange={(event) =>
                onChange(
                  setSessionIntroText(
                    parts,
                    document.documentId,
                    "greeting_template",
                    event.target.value,
                  ),
                )
              }
            />
          )}
        </Field>

        <Field
          label="Opening question"
          description="What the opening turn actually asks. Required when the opening turn is on — a turn that asks nothing stalls the interview on its first message, and the Editor Service refuses it."
        >
          {({ inputId, describedBy }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              rows={3}
              dir="auto"
              value={view.sessionIntro.ask}
              readOnly={readOnly}
              onChange={(event) =>
                onChange(
                  setSessionIntroText(
                    parts,
                    document.documentId,
                    "ask",
                    event.target.value,
                  ),
                )
              }
            />
          )}
        </Field>
      </div>

      <div className="space-y-4">
        <Group className="text-sm font-medium text-foreground">
          Background opening
        </Group>
        <Field
          label="First message of the session"
          description="Placed in front of the very first question the interview asks, and only that one. {firstname} is replaced with the patient's first name. Leave it empty for a profile that carries its greeting in the opening turn above instead."
        >
          {({ inputId, describedBy }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              rows={12}
              dir="auto"
              value={view.bgIntro}
              readOnly={readOnly}
              onChange={(event) =>
                onChange(
                  setBgIntro(parts, document.documentId, event.target.value),
                )
              }
            />
          )}
        </Field>
      </div>

      <div className="space-y-4">
        <Group className="text-sm font-medium text-foreground">
          Moving between background areas
        </Group>
        {/* WHAT COLLAPSING WOULD COST, said BEFORE the control rather than in a
            modal after it. One shared instruction can keep only one of the
            three clauses, and the surface owes that sentence to a clinician who
            is about to choose it. It is not a confirmation step: this is a
            DRAFT edit like every other on this page — `Discard` restores it and
            nothing is published until Save. Expanding the other way is not
            lossy at all, since the runtime already used the one string for
            every kind. */}
        {view.shape === "clauses" && writtenClauses(view) > 1 ? (
          <Notice>
            One instruction for every transition would keep only the first of
            the {writtenClauses(view)} transitions written below; the rest are
            dropped from the draft. Discard restores them until this is saved.
          </Notice>
        ) : null}

        <Field label="How transitions are written" description={SHAPE_DESCRIPTION}>
          {({ inputId, describedBy }) => (
            <Select
              id={inputId}
              aria-describedby={describedBy}
              value={view.shape}
              disabled={readOnly}
              onChange={(event) =>
                onChange(
                  setTransitionShape(
                    parts,
                    document.documentId,
                    event.currentTarget.value === "single"
                      ? "single"
                      : "clauses",
                  ),
                )
              }
            >
              <option value="clauses">Three separate transitions</option>
              <option value="single">One instruction for every transition</option>
            </Select>
          )}
        </Field>

        {view.shape === "single" ? (
          <Field
            label="Transition instruction"
            description="Used for all three kinds of move between areas. {domain_display} is replaced with the name of the area being moved to."
          >
            {({ inputId, describedBy }) => (
              <Textarea
                id={inputId}
                aria-describedby={describedBy}
                rows={6}
                dir="auto"
                value={view.single}
                readOnly={readOnly}
                onChange={(event) =>
                  onChange(
                    setTransitionSingle(
                      parts,
                      document.documentId,
                      event.target.value,
                    ),
                  )
                }
              />
            )}
          </Field>
        ) : (
          TRANSITION_KINDS.map((kind) => (
            <Field
              key={kind}
              label={TRANSITION_LABELS[kind]}
              description={`${TRANSITION_DESCRIPTIONS[kind]} {domain_display} is replaced with the name of the area being moved to; leaving it empty suppresses this clause.`}
            >
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  rows={5}
                  dir="auto"
                  value={view.clauses[kind]}
                  readOnly={readOnly}
                  onChange={(event) =>
                    onChange(
                      setTransitionClause(
                        parts,
                        document.documentId,
                        kind,
                        event.target.value,
                      ),
                    )
                  }
                />
              )}
            </Field>
          ))
        )}
      </div>

      <BannedPhrases
        phrases={view.bannedPhrases}
        heading={Group}
        readOnly={readOnly}
        onChange={(phrases) =>
          onChange(setBannedPhrases(parts, document.documentId, phrases))
        }
      />
    </section>
  );
}

/**
 * The ordered list of phrases the profile records as unwanted.
 *
 * TEXT INPUTS RATHER THAN ONE COMMA-SEPARATED BOX: a phrase may legitimately
 * contain any punctuation, and a separator would make some phrases unwritable.
 * Reorder is buttons, on `NamedRowList`'s rule — the list is ordered by contract
 * and two buttons are operable by keyboard, pointer and assistive technology on
 * the first day.
 */
function BannedPhrases({
  phrases,
  heading: Heading,
  readOnly,
  onChange,
}: {
  phrases: readonly string[];
  /** The group heading level this form's caller computed. */
  heading: "h2" | "h3";
  readOnly: boolean;
  onChange: (phrases: string[]) => void;
}) {
  function move(from: number, to: number) {
    if (to < 0 || to >= phrases.length) {
      return;
    }
    const next = [...phrases];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved as string);
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <Heading className="text-sm font-medium text-foreground">
        Phrases to avoid
      </Heading>
      {/* THE LIMIT IS STATED, not implied. Nothing in the pinned engine reads
          this list; the phrases are actually kept out by the transition
          instructions above, which name them. Saying so is what stops a
          clinician from believing a phrase is suppressed because it is here. */}
      <Notice>
        These phrases are carried with the profile and exported with it, but the
        pinned clinical engine does not filter on them. What actually keeps a
        phrase out of the conversation is the transition instructions above
        naming it.
      </Notice>

      {phrases.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No phrases are recorded on this profile.
        </p>
      ) : (
        <ol className="space-y-2">
          {phrases.map((phrase, index) => (
            // Keyed on the position: a phrase list is plain strings with no
            // identity of its own, two entries are legitimately equal mid-edit,
            // and keying on the text would make React reuse the wrong row's DOM
            // — including the focused input — the moment they collided.
            <li key={index} className="flex items-center gap-2">
              <span className="w-6 shrink-0 text-xs tabular-nums text-muted-foreground">
                {index + 1}.
              </span>
              <Input
                aria-label={`Phrase to avoid ${index + 1}`}
                dir="auto"
                className="flex-1"
                value={phrase}
                readOnly={readOnly}
                onChange={(event) => {
                  const next = [...phrases];
                  next[index] = event.target.value;
                  onChange(next);
                }}
              />
              {readOnly ? null : (
                <div className="flex items-center gap-0.5">
                  <IconButton
                    label={`Move phrase ${index + 1} up`}
                    icon={<ArrowUp className="h-4 w-4" />}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  />
                  <IconButton
                    label={`Move phrase ${index + 1} down`}
                    icon={<ArrowDown className="h-4 w-4" />}
                    disabled={index === phrases.length - 1}
                    onClick={() => move(index, index + 1)}
                  />
                  <IconButton
                    label={`Remove phrase ${index + 1}`}
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() =>
                      onChange(phrases.filter((_, at) => at !== index))
                    }
                  />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {readOnly ? null : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange([...phrases, ""])}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Add phrase
        </Button>
      )}
    </div>
  );
}
